/* ============================================================
   mg-cart-modal.js — SHC-style "Added to your cart" modal for MG.
   1. Heading: "Added to your cart" right after an add, "Your cart"
      when opened from the cart icon (html[data-mg-cart-added]).
   2. <mg-cart-recs>: "Pairs great with" rail from Shopify's
      recommendations for the latest cart item (excludes items
      already in the cart). Cart re-renders (Horizon morph) can empty
      the rail, so it watches the dialog and rebuilds from a cache,
      reading the hidden [data-mg-cart-state] node for the current cart.
   ============================================================ */

const ADDED_ATTR = 'data-mg-cart-added';
const ADDED_WINDOW_MS = 5000;
let lastAddAt = 0;

function markAdded() {
  lastAddAt = Date.now();
  document.documentElement.setAttribute(ADDED_ATTR, '');
}

import('@shopify/events')
  .then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (event.action === 'add') markAdded();
    });
  })
  .catch(() => {});

document.addEventListener('theme-drawer:open', (event) => {
  if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
  // The add event can fire just after the drawer opens — check once it settles.
  setTimeout(() => {
    if (Date.now() - lastAddAt > ADDED_WINDOW_MS) {
      document.documentElement.removeAttribute(ADDED_ATTR);
    }
  }, 150);
});

document.addEventListener('theme-drawer:close', (event) => {
  if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
  setTimeout(() => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer?.hasAttribute('open')) document.documentElement.removeAttribute(ADDED_ATTR);
  }, 400);
});

/* ---------------- Recommendations rail ---------------- */

/** @type {Map<string, Promise<any[]>>} */
const recCache = new Map();

function fetchRecs(productId) {
  if (!recCache.has(productId)) {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=related`;
    recCache.set(
      productId,
      fetch(url)
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((j) => j.products || [])
        .catch(() => [])
    );
  }
  return recCache.get(productId);
}

function money(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return '$' + (cents / 100).toFixed(2);
  }
}

function esc(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function imgUrl(src, width) {
  if (!src) return '';
  const url = src.startsWith('//') ? 'https:' + src : src;
  return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
}

function cardHtml(p) {
  const price = p.price_min ?? p.price;
  const compare = p.compare_at_price_min ?? p.compare_at_price;
  const img = p.featured_image || p.images?.[0] || '';
  const from = p.price_varies ? 'From ' : '';
  const strike = compare && compare > price
    ? `<s><span class="visually-hidden">Regular price</span>${money(compare)}</s>`
    : '';
  return `<li class="mg-cm-rec">
    <a class="mg-cm-rec__image" href="${esc(p.url)}" tabindex="-1" aria-hidden="true">
      ${img ? `<img src="${esc(imgUrl(img, 540))}" srcset="${esc(imgUrl(img, 360))} 360w, ${esc(imgUrl(img, 540))} 540w, ${esc(imgUrl(img, 720))} 720w" sizes="(max-width: 749px) 45vw, 220px" alt="" loading="lazy" width="540" height="540">` : ''}
    </a>
    <a class="mg-cm-rec__title" href="${esc(p.url)}">${esc(p.title)}</a>
    <div class="mg-cm-rec__price"><span>${from}${money(price)}</span>${strike}</div>
  </li>`;
}

class MgCartRecs extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;
  #key = '';
  #timer = 0;
  #noResults = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    // Capture-phase so it survives the rail node being replaced by a morph.
    this.addEventListener('scroll', this.#updateArrows, { capture: true, passive: true });
    const scope = this.closest('dialog') || document;
    this.#observer = new MutationObserver(this.#schedule);
    this.#observer.observe(scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-product-id', 'data-cart-product-ids'],
    });
    this.render();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #schedule = () => {
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.render(), 60);
  };

  get #rail() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.mg-cm-recs__rail'));
  }

  async render() {
    const state = /** @type {HTMLElement | null} */ (
      (this.closest('dialog') || document).querySelector('[data-mg-cart-state]')
    );
    const rail = this.#rail;
    if (!state || !rail) return;

    const productId = state.dataset.productId || '';
    const inCartIds = state.dataset.cartProductIds || '';
    const key = productId + '|' + inCartIds;
    // Same cart + rail still filled → nothing to do. (A cart re-render can
    // empty the rail even though the key is unchanged; then rebuild from cache.)
    if (key === this.#key && (this.#noResults ? this.hidden : rail.childElementCount && !this.hidden)) return;
    this.#key = key;

    if (!productId) {
      this.#noResults = true;
      this.hidden = true;
      return;
    }

    const inCart = new Set(inCartIds.split(',').filter(Boolean));
    const products = (await fetchRecs(productId)).filter((p) => !inCart.has(String(p.id)) && p.available !== false);
    if (key !== this.#key) return; // a newer render started meanwhile

    this.#noResults = !products.length;
    if (!products.length) {
      this.hidden = true;
      return;
    }
    rail.innerHTML = products.slice(0, 8).map(cardHtml).join('');
    rail.scrollLeft = 0;
    this.hidden = false;
    requestAnimationFrame(this.#updateArrows);
  }

  #updateArrows = () => {
    const rail = this.#rail;
    if (!rail) return;
    const max = rail.scrollWidth - rail.clientWidth - 2;
    const prev = this.querySelector('[data-mg-rec-dir="-1"]');
    const next = this.querySelector('[data-mg-rec-dir="1"]');
    if (prev instanceof HTMLButtonElement) prev.disabled = rail.scrollLeft <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = rail.scrollLeft >= max;
    this.toggleAttribute('data-no-scroll', max <= 0);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-mg-rec-dir]') : null;
    const rail = this.#rail;
    if (!btn || !rail) return;
    const dir = Number(btn.getAttribute('data-mg-rec-dir'));
    rail.scrollBy({ left: dir * rail.clientWidth * 0.9, behavior: 'smooth' });
  };
}

if (!customElements.get('mg-cart-recs')) {
  customElements.define('mg-cart-recs', MgCartRecs);
}
